import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { usePutUserDisplayName, usePutUserLocale } from '@/api/generated/user/user';
import type { LocaleEnum, LoginResponse } from '@/api/types/auth';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { reconcileLocaleFromUser } from '@/config/i18n/i18n';
import {
  DEFAULT_LOCALE_TAG,
  LANGUAGE_OPTIONS,
  LOCALE_STORAGE_KEY,
  type LocaleTag,
  isSupportedTag,
} from '@/config/i18n/locale';
import { queryKeys } from '@/config/query/queryKeys';
import { USER_NAME_LOCAL_STORAGE_KEY } from '@/utils/constants';

import { useFirstRunProfileGate } from '../hooks/useFirstRunProfileGate';

export function FirstRunProfileGate() {
  const { isOpen, needsDisplayName, needsLocale, user } = useFirstRunProfileGate();
  const { i18n, t } = useTranslation();
  const queryClient = useQueryClient();
  const updateLocaleMutation = usePutUserLocale();
  const updateDisplayNameMutation = usePutUserDisplayName();

  const initialTag = isSupportedTag(i18n.resolvedLanguage ?? i18n.language)
    ? ((i18n.resolvedLanguage ?? i18n.language) as LocaleTag)
    : DEFAULT_LOCALE_TAG;
  const [selected, setSelected] = useState<LocaleTag>(initialTag);
  const [name, setName] = useState<string>(user?.displayName?.trim() ?? '');

  const trimmedName = name.trim();
  const isPending = updateLocaleMutation.isPending || updateDisplayNameMutation.isPending;
  const canSubmit = (!needsDisplayName || trimmedName.length > 0) && !isPending;

  const handleConfirm = async () => {
    const tasks: Array<Promise<unknown>> = [];
    let nextLocale: LocaleEnum | null | undefined;
    let nextDisplayName: string | undefined;

    if (needsLocale) {
      window.localStorage?.setItem(LOCALE_STORAGE_KEY, selected);
      void i18n.changeLanguage(selected);
      tasks.push(
        updateLocaleMutation
          .mutateAsync({ data: { locale: selected } })
          .then((res) => {
            if (res.locale) {
              nextLocale = res.locale;
              reconcileLocaleFromUser(res.locale);
            }
          })
          .catch((error: Error) => {
            toast.error(error.message ?? 'Could not save language preference');
            throw error;
          }),
      );
    }

    if (needsDisplayName) {
      tasks.push(
        updateDisplayNameMutation
          .mutateAsync({ data: { displayName: trimmedName } })
          .then((res) => {
            if (res.displayName) {
              nextDisplayName = res.displayName;
              window.localStorage?.setItem(USER_NAME_LOCAL_STORAGE_KEY, res.displayName);
            }
          })
          .catch((error: Error) => {
            toast.error(error.message ?? 'Could not save display name');
            throw error;
          }),
      );
    }

    try {
      await Promise.all(tasks);
      queryClient.setQueryData<LoginResponse['user'] | undefined>(queryKeys.auth.user(), (prev) =>
        prev
          ? {
              ...prev,
              ...(nextDisplayName !== undefined ? { displayName: nextDisplayName } : {}),
              ...(nextLocale !== undefined ? { locale: nextLocale } : {}),
            }
          : prev,
      );
    } catch {
      // toasts already shown above; mutation error state keeps the dialog open
    }
  };

  const title =
    needsLocale && needsDisplayName
      ? t('firstRunGate.combined.title', 'Welcome to Lucio')
      : needsDisplayName
        ? t('firstRunGate.displayName.title', "What's your name?")
        : t('firstRunGate.locale.title', 'Pick your language');

  const description =
    needsLocale && needsDisplayName
      ? t(
          'firstRunGate.combined.description',
          'Tell us your name and pick a language so Lucio can personalise replies. You can change these later in your profile.',
        )
      : needsDisplayName
        ? t(
            'firstRunGate.displayName.description',
            'Lucio will use this name to address you in greetings and drafts. You can change it later in your profile.',
          )
        : t(
            'firstRunGate.locale.description',
            'Lucio will reply, format dates, and write summaries in this language. You can change it later in your profile.',
          );

  return (
    <Dialog open={isOpen}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 py-2">
          {needsDisplayName && (
            <div className="flex flex-col gap-1.5">
              <label
                className="text-foreground text-[12px] font-medium"
                htmlFor="first-run-display-name"
              >
                {t('firstRunGate.displayName.label', 'Display name')}
              </label>
              <Input
                autoFocus
                id="first-run-display-name"
                onChange={(e) => setName(e.target.value)}
                placeholder={t('firstRunGate.displayName.placeholder', 'Your name')}
                value={name}
              />
            </div>
          )}
          {needsLocale && (
            <div className="flex flex-col gap-2">
              {LANGUAGE_OPTIONS.map((option) => (
                <button
                  aria-pressed={selected === option.value}
                  className={`flex h-11 items-center justify-between rounded-lg border px-3 text-left text-[13px] font-medium transition-colors ${
                    selected === option.value
                      ? 'border-primary bg-back-bg2'
                      : 'border-border-subtle hover:bg-back-bg2'
                  }`}
                  key={option.value}
                  onClick={() => setSelected(option.value)}
                  type="button"
                >
                  <span>{option.label}</span>
                  {selected === option.value ? <span aria-hidden>✓</span> : null}
                </button>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button disabled={!canSubmit} onClick={() => void handleConfirm()} type="button">
            {isPending
              ? t('firstRunGate.locale.saving', 'Saving…')
              : t('firstRunGate.locale.confirm', 'Continue')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
